import { useCallback, useEffect, useState } from 'react';
import { isAxiosError } from 'src/api/instance';
import api from '../api';
import { ReviewState } from '../context/AssetVerificationReviewProvider';
import { logRecalculateFailed } from '../observability';
import { buildRecalculatePayload } from '../utils';

// FVS-computed pre-submit preview (contract §6, ITL-3075): the summary total must
// come from the same arithmetic complete will store — the FE never computes assets
// (2026-08-03 design change). Stateless on the FVS side, so refetching is safe.
// `enabled` gates on hydration + zero submit violations: the payload FVS would
// 400 on never leaves the client. A preview failure never blocks submit — FVS
// recomputes authoritatively on complete.
export const useRecalculateAssets = (entryId: string, state: ReviewState, enabled: boolean) => {
  const [total, setTotal] = useState<number | null>(null);
  const [hasVerifiedNothing, setHasVerifiedNothing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setIsLoading(true);
    setIsError(false);
    api
      .recalculateReview(entryId, buildRecalculatePayload(state))
      .then((response) => {
        if (cancelled) return;
        // null is NOT zero: it means no statement produced a verifiable balance
        // (every statement excluded, or none had a usable balance). Rendering it
        // as $0.00 tells the analyst FVS computed a total when it computed none.
        const raw = response.total_verified_assets;
        if (raw === null || raw === undefined) {
          setTotal(null);
          setHasVerifiedNothing(true);
          return;
        }
        const value = Number(raw);
        if (!Number.isFinite(value)) {
          // A total that is present but unparseable is a contract violation, not
          // "not calculated yet" — surface it as an error so it isn't silent.
          setIsError(true);
          logRecalculateFailed(
            { entry_id: entryId, applicant_id: state.applicantId },
            { error_type: 'malformed_response' }
          );
          return;
        }
        setHasVerifiedNothing(false);
        setTotal(value);
      })
      .catch((error) => {
        if (cancelled) return;
        setIsError(true);
        logRecalculateFailed(
          { entry_id: entryId, applicant_id: state.applicantId },
          isAxiosError(error) && error.response
            ? {
                error_type: error.response.status === 400 ? 'validation_error' : 'unknown_error',
                http_status: error.response.status
              }
            : { error_type: isAxiosError(error) ? 'network_error' : 'unknown_error' }
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `state` is intentionally read at call time only: the summary page never edits
    // the panel, so re-running on every state identity change would only duplicate
    // calls. `attempt` drives explicit retries.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, entryId, attempt]);

  const retry = useCallback(() => setAttempt((count) => count + 1), []);

  return { total, hasVerifiedNothing, isLoading, isError, retry };
};

export default useRecalculateAssets;
